import * as XLSX from 'xlsx';
import { SummaryRow } from '../../../shared/services/humanResources/types';

/**
 * Exportacion de la pestaña "Resumen" (Horas Extra) a Excel/CSV - reemplaza
 * el envio manual de los 15 archivos "CALCULO TIEMPO EXTRA-v2.xlsm" (uno por
 * lider/coordinador) a Contabilidad. Mismo layout de columnas que la hoja
 * RESUMEN del Excel original, para que el equipo de Contabilidad reconozca
 * el formato de inmediato.
 *
 * Patron tomado de apps/web/src/modules/consolidado/utils/exportConsolidado.ts
 * (independiente de esa exportacion - vive solo dentro de este modulo).
 */

function formatCurrencyPlain(n: number): number {
  return Math.round(n);
}

function toExportRows(rows: SummaryRow[]) {
  return rows.map((r) => ({
    Cédula: r.documentNumber,
    Nombre: r.employeeFullName,
    Temporal: r.temporalCompany ?? '',
    Cargo: r.position ?? '',
    Salario: formatCurrencyPlain(r.monthlySalary),
    'Valor Hora': Math.round(r.hourlyRate),
    'Horas Asignadas': r.assignedHours,
    'Horas Laboradas': r.operativeHours,
    'Tiempo Extra': Number((r.operativeHours - r.assignedHours).toFixed(2)),
    'Hora Extra Diurna Ordinaria': r.extraDiurnalOrdinaryHours,
    'Valor HEDO': formatCurrencyPlain(r.extraDiurnalOrdinaryPay),
    'Hora Extra Nocturna Ordinaria': r.extraNocturnalOrdinaryHours,
    'Valor HENO': formatCurrencyPlain(r.extraNocturnalOrdinaryPay),
    'Extra Diurna Festiva': r.extraDiurnalFestiveHours,
    'Valor HEDF': formatCurrencyPlain(r.extraDiurnalFestivePay),
    'Extra Nocturna Festiva': r.extraNocturnalFestiveHours,
    'Valor HENF': formatCurrencyPlain(r.extraNocturnalFestivePay),
    '# Total Tiempo Extra': r.totalExtraHours,
    '$ Total Tiempo Extra': formatCurrencyPlain(r.totalExtraPay),
    'Recargo Nocturno Ordinario': r.nightSurchargeHours,
    'Valor RNO': formatCurrencyPlain(r.nightSurchargePay),
    'Recargo Nocturno Festivo Con Compensatorio': r.nightFestiveWithCompHours,
    'Valor RNFCC': formatCurrencyPlain(r.nightFestiveWithCompPay),
    'Recargo Nocturno Festivo Sin Compensatorio': r.nightFestiveWithoutCompHours,
    'Valor RNFSC': formatCurrencyPlain(r.nightFestiveWithoutCompPay),
    '# Total Recargos': r.totalSurchargeHours,
    '$ Total Recargos': formatCurrencyPlain(r.totalSurchargePay),
    'Festivo Con Compensatorio': r.festiveWithCompHours,
    'Valor FCC': formatCurrencyPlain(r.festiveWithCompPay),
    'Festivo Sin Compensatorio': r.festiveWithoutCompHours,
    'Valor FSC': formatCurrencyPlain(r.festiveWithoutCompPay),
    'Total Pago': formatCurrencyPlain(r.totalPay),
  }));
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function periodStamp(dateFrom: string, dateTo: string) {
  return `${dateFrom}_a_${dateTo}`;
}

const COLUMN_WIDTHS = [
  12, 28, 12, 22, 12, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 12, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 14,
].map((wch) => ({ wch }));

/** Descarga un .xlsx con una sola hoja "Resumen", con el total general al final. */
export function exportSummaryExcel(rows: SummaryRow[], dateFrom: string, dateTo: string) {
  const exportRows = toExportRows(rows);
  const totalRow: Record<string, string | number> = { Cédula: '', Nombre: 'TOTAL GENERAL' };
  const numericKeys = Object.keys(exportRows[0] ?? {}).filter((k) => !['Cédula', 'Nombre', 'Temporal', 'Cargo'].includes(k));
  for (const key of numericKeys) {
    totalRow[key] = exportRows.reduce((sum, r) => sum + (Number((r as any)[key]) || 0), 0);
  }
  const sheet = XLSX.utils.json_to_sheet([...exportRows, totalRow]);
  sheet['!cols'] = COLUMN_WIDTHS;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, 'Resumen');
  XLSX.writeFile(wb, `resumen_horas_extra_${periodStamp(dateFrom, dateTo)}.xlsx`);
}

/** Descarga un .csv con el mismo detalle (para importar en cualquier otra herramienta). */
export function exportSummaryCsv(rows: SummaryRow[], dateFrom: string, dateTo: string) {
  const sheet = XLSX.utils.json_to_sheet(toExportRows(rows));
  const csv = XLSX.utils.sheet_to_csv(sheet);
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `resumen_horas_extra_${periodStamp(dateFrom, dateTo)}.csv`);
}
